import React, { useContext, useEffect, useMemo, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { SubsectionAccordionItemComponent } from 'src/components/Reusable/Accordions/ShowPage/Subsections/SubsectionAccordion.components'
import { FlexAlign, FlexColumn, FlexRow } from 'src/components/Reusable/Flex'
import {
  ANSWER_ACCEPTANCE,
  ANSWER_STATUS,
  ANSWER_TYPE,
  Question,
  QUESTIONNAIRE_TYPE,
} from '@trustero/trustero-api-web/lib/questionnaire/questionnaire_pb'
import log from 'loglevel'
import { useModalState } from 'src/Modal/ModalStateContext'
import { ModalFormId, useConfirmationModal } from 'src/components/ModalForms'
import { AICopilotLoading } from 'src/pages/Controls/ControlsShowPage/ControlsShowItem/ControlsShowItemAccordions/ControlsShowItemGuidance/ControlsShowItemGuidance.components'
import { Spinner } from 'src/Throbber'
import { ErrorSVG } from 'src/components/Icons/Basic'
import palette from 'src/designSystem/variables/palette'
import { useIsBuyer } from 'src/context/authContext'
import { TextButton } from 'src/components/Reusable/Buttons/TextButton'
import { useTypewriter } from 'src/Utils/globalHooks'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faChevronDown,
  faChevronUp,
  faRefresh,
} from '@fortawesome/free-solid-svg-icons'
import {
  Source,
  SOURCE_TYPE,
} from '@trustero/trustero-api-web/lib/source/source_pb'
import {
  ChatCitation,
  ChatContextSourceType,
} from '@trustero/trustero-api-web/lib/chatcontext/chatcontext_pb'
import { P } from 'src/components/Reusable/Text/Text.styles'
import { showInfoToast } from 'src/Utils/helpers/toast'
import { formatISODateTime, getHumanReadableWall } from 'src/Utils/formatDate'
import { PERMISSIONS } from 'src/config/roleConfig'
import { AccordionContext } from 'react-bootstrap'
import { isAccordionItemSelected } from 'react-bootstrap/esm/AccordionContext'
import { AllContexts } from 'src/pages/TrusteroIntelligence/chatcontextpanel/categoryselector/CategorySelector'
import { ContextTypeSummaryItemIcon } from 'src/pages/TrusteroIntelligence/chatcontextpanel/chatContextPanel.styles'
import { TableListLoader } from 'src/components/Reusable/ContentLoaders/TableListLoader'
import {
  DocumentSourceLinkIcon,
  DocumentSourcePolicyIcon,
  DocumentSourceRelevantDate,
  SourceContainer,
  SourceDeleted,
  SourceSummary,
} from 'src/components/Reusable/Sources/sources.styles'
import { Markup } from 'src/components/Reusable/Text/Markup'
import { getIsDocumentSource } from 'src/components/Reusable/Sources/sources.helpers'
import { useDocumentSourceContext } from 'src/context/DocumentSource/DocumentSourceContext'
import { Tooltip } from 'src/components/Reusable/Tooltip'
import {
  useAnswerQuestionAgain,
  useAnswerSources,
  useCitationLink,
  useGetQuestionnaire,
  useUpdateAnswerFromQuestion,
} from '../securityQuestionnaire.hooks'
import {
  ActionContainer,
  EroDefaultAnswer,
  QuestionnairePrimaryButton,
  QuestionnaireSecondaryButton,
} from './securityQuestionnaireShowPage.components'
import {
  AccordionTitle,
  AccordionTitleBold,
  QuestionnaireAnswerTitle,
  QuestionnaireCheckIcon,
  QuestionnaireP,
  QuestionnaireQuestionMarkIcon,
  StyledScanAgainContainer,
  StyledScanAgainP,
  QASourcesContainer,
  PageNumberLabel,
  PageNumberItemLabel,
  PageNumberItemLabelLink,
} from './securityQuestionnaireShowPage.styles'
import { useCurrentQuestion } from './context/currentQuestionContext'
import { getReadableAnswerType } from './securityQuestionnaireShowPage.constants'

const AccordionTitleHeader = ({
  inProgress,
  item,
}: {
  inProgress: boolean
  item: number
}): JSX.Element =>
  inProgress ? (
    <FlexRow gap={5}>
      <Spinner size="xs" color="secondary" />
      <AccordionTitleBold>
        (Answering...) Question #{`${item}`}
      </AccordionTitleBold>
    </FlexRow>
  ) : (
    <AccordionTitleBold>Question #{`${item}`}</AccordionTitleBold>
  )

const QuestionnaireAnswerIcon = ({
  question,
}: {
  question: Question
}): JSX.Element => {
  const canAnswer = question.getCanAnswer()
  const isGenerated = question.getStatus() === ANSWER_STATUS.GENERATED
  if (isGenerated && !canAnswer) {
    const hasFailed = question.getType() === ANSWER_TYPE.UNSUCCESSFUL
    return (
      <ErrorSVG
        style={{ width: '16px', height: '16px' }}
        fill={hasFailed ? `${palette.orange[400]}` : `${palette.magenta[400]}`}
      />
    )
  }
  const isAccepted = question.getAcceptance() === ANSWER_ACCEPTANCE.ACCEPTED
  return isAccepted ? (
    <QuestionnaireCheckIcon />
  ) : (
    <QuestionnaireQuestionMarkIcon />
  )
}

export const SecurityQuestionnaireAccordionItem = ({
  question,
  eventKey,
  idx,
  toggleOpen,
  isAcceptedQuestionnaire,
  hasChatContext,
}: {
  question: Question
  eventKey: string
  idx: number
  toggleOpen: (eventKey: string) => void
  isAcceptedQuestionnaire: boolean
  hasChatContext: boolean
}): JSX.Element => {
  const questionText = question.getQuestion()

  return (
    <SubsectionAccordionItemComponent
      handleHeaderClick={() => toggleOpen(eventKey)}
      handleBodyClick={(e) => {
        // this is needed to prevent the header from responding to body clicks
        e.stopPropagation()
      }}
      eventKey={eventKey}
      title={
        <AccordionTitleHeader
          inProgress={question.getInProgress()}
          item={idx + 1}
        />
      }
      notes={
        <AccordionTitle onClick={(e) => e.stopPropagation()}>
          {questionText}
        </AccordionTitle>
      }
      body={
        <QuestionnaireAccordionBody
          isAcceptedQuestionnaire={isAcceptedQuestionnaire}
          question={question}
          hasChatContext={hasChatContext}
        />
      }
      icon={<QuestionnaireAnswerIcon question={question} />}
      $customGridLayout="min-content max-content 1fr"
    />
  )
}

export const QuestionnaireAccordionButtons = ({
  question,
  isAccepted,
  disableAccept,
}: {
  isAccepted: boolean
  question: Question
  disableAccept?: boolean
}): JSX.Element => {
  const updateAnswer = useUpdateAnswerFromQuestion()
  const { questionnaireId } = useCurrentQuestion()
  const { openModal } = useModalState()
  const { setCurrentQuestion } = useCurrentQuestion()
  const { mutate } = useGetQuestionnaire({
    id: questionnaireId,
  })

  const editAnswer = () => {
    setCurrentQuestion(question)
    openModal(ModalFormId.EDIT_ANSWER)
  }

  const acceptAnswer = async () => {
    try {
      question.setAcceptance(ANSWER_ACCEPTANCE.ACCEPTED)
      await updateAnswer(question)
      await mutate()
    } catch (err) {
      log.error(
        `Error updating answer acceptance in questionId: ${questionnaireId}`,
        err,
      )
    }
  }
  const inProgress = question.getInProgress()

  return (
    <FlexColumn isFullWidth>
      {!isAccepted && (
        <QuestionnairePrimaryButton
          text="Accept"
          onClick={acceptAnswer}
          isDisabled={inProgress || disableAccept}
        />
      )}
      <QuestionnaireSecondaryButton
        text="Edit Answer"
        onClick={editAnswer}
        isDisabled={inProgress}
      />
    </FlexColumn>
  )
}

export const QuestionnaireAccordionBody = ({
  question,
  isAcceptedQuestionnaire,
  hasChatContext,
}: {
  question: Question
  isAcceptedQuestionnaire: boolean
  hasChatContext: boolean
}): JSX.Element => {
  const answerAgain = useAnswerQuestionAgain(question)
  const handleAnswerAgain = async () => {
    try {
      await answerAgain()
    } catch (err) {
      log.error(
        `Error answering question again questionId: ${question.getId()} err:`,
        err,
      )
    }
  }
  const confirmOverwriteAccepted = useConfirmationModal({
    title: 'Warning, this response has already been accepted.',
    body: 'Are you sure you want to generate a new answer? Generating a new answer will erase the previous response.',
    confirmText: 'Answer Again',
    handleSubmit: handleAnswerAgain,
  })
  const confirmOverwriteManual = useConfirmationModal({
    title: 'You have already entered a manual response.',
    body: 'Do you want to proceed? Answering again will erase your response.',
    confirmText: 'Answer Again',
    handleSubmit: handleAnswerAgain,
  })
  const confirmScanAgainOnFailure = useConfirmationModal({
    title: "Trustero AI wasn't able to generate an answer.",
    body: 'It is possible, if you update your environment that a new response may be generated.',
    confirmText: 'Answer Again',
    handleSubmit: handleAnswerAgain,
  })

  const handleAnswerAgainClick = () => {
    if (question.getAcceptance() === ANSWER_ACCEPTANCE.ACCEPTED) {
      confirmOverwriteAccepted()
    } else if (question.getStatus() === ANSWER_STATUS.USER_EDITED) {
      confirmOverwriteManual()
    } else if (
      question.getStatus() === ANSWER_STATUS.GENERATED &&
      question.getType() === ANSWER_TYPE.UNSUCCESSFUL
    ) {
      confirmScanAgainOnFailure()
    } else {
      handleAnswerAgain()
    }
  }

  const hasKbSources = question.getHasKbSources()
  const canAnswer = question.getCanAnswer()
  const status = question.getStatus()
  const isManualEdit = status === ANSWER_STATUS.USER_EDITED
  const isAccepted = question.getAcceptance() === ANSWER_ACCEPTANCE.ACCEPTED
  const inProgress = question.getInProgress()
  const disableAccept =
    question.getInProgress() || (!canAnswer && !isManualEdit)

  return (
    <FlexColumn
      isFullWidth
      align={FlexAlign.FLEX_START}
      justify={FlexAlign.FLEX_START}
    >
      <FlexRow
        align={FlexAlign.FLEX_START}
        justify={FlexAlign.FLEX_START}
        gap={20}
      >
        <ActionContainer
          body={
            isAcceptedQuestionnaire ? (
              <></>
            ) : (
              <QuestionnaireAccordionButtons
                isAccepted={isAccepted}
                question={question}
                disableAccept={disableAccept}
              />
            )
          }
          isManual={isManualEdit}
        />
        <QuestionnaireAccordionAnswer
          question={question}
          hasKbSources={hasKbSources}
        />
      </FlexRow>
      {!inProgress && !isAcceptedQuestionnaire && !hasChatContext && (
        <AnswerAgainButton onClickFunc={handleAnswerAgainClick} />
      )}
    </FlexColumn>
  )
}

const AnswerCitation = ({
  citation,
  isAgentChat,
}: {
  citation: ChatCitation
  isAgentChat?: boolean
}): JSX.Element => {
  const { openModal } = useModalState()
  const { setCurrentDocumentSource } = useDocumentSourceContext()
  const citationLink = useCitationLink(citation)
  const caption = citation.getCaption()
  const sourceType = citation.getType()
  const date = citation.getRelevantDate()
  const isDeleted = citation.getIsDeleted()
  const isDocumentSource =
    sourceType === ChatContextSourceType.DOCUMENT_GROUP ||
    sourceType === ChatContextSourceType.KNOWLEDGE_BASE ||
    sourceType === ChatContextSourceType.ARTIFACT_DOCUMENT ||
    sourceType == ChatContextSourceType.CHAT_UPLOAD_DOCUMENT ||
    (sourceType === ChatContextSourceType.SUPPORT_KNOWLEDGE &&
      !!citation.getContentId()) ||
    (sourceType === ChatContextSourceType.COMPANY &&
      !caption.startsWith('Scope Info for account -'))
  const onClick = () => {
    if (isDocumentSource) {
      setCurrentDocumentSource(citation)
      openModal(ModalFormId.VIEW_DOCUMENT_SOURCE)
    }
  }

  const contextTypeInfo = useMemo(
    () => AllContexts.find((context) => context.type === citation.getType()),
    [citation],
  )

  const icon = useMemo(() => contextTypeInfo?.icon, [contextTypeInfo])

  const tooltipBody = useMemo(
    () => `${caption} (${contextTypeInfo?.label})`,
    [caption, contextTypeInfo],
  )

  // SOC2 Report Summary feature is hidden — skip rendering its citations
  if (sourceType === ChatContextSourceType.SOC2_REPORT_SUMMARY) {
    return <></>
  }

  return (
    <SourceContainer $isAgentChat={isAgentChat}>
      <FlexRow gap={8} justify={FlexAlign.FLEX_START}>
        <Tooltip
          key={citation.getType()}
          id={`tooltip-context-edit-${citation.getType()}`}
          trigger={['hover', 'click']}
          tooltipBody={tooltipBody}
          placement="left"
        >
          <ContextTypeSummaryItemIcon $small>{icon}</ContextTypeSummaryItemIcon>
        </Tooltip>
        <span>{caption}</span>
        {date && (
          <DocumentSourceRelevantDate $isAgentChat={isAgentChat}>
            {formatISODateTime(new Date(date))}
          </DocumentSourceRelevantDate>
        )}
        {isDeleted ? (
          <SourceDeleted>(Deleted)</SourceDeleted>
        ) : isDocumentSource ? (
          <CitationLink
            isAgentChat={isAgentChat}
            mimeType={citation.getMimetype()}
            citationType={{
              onClick,
              pageNumbers: citation.getPageNumber()
                ? [
                    {
                      pageNumber: citation.getPageNumber(),
                      onClick,
                    },
                  ]
                : undefined,
            }}
          />
        ) : (
          <CitationLink
            isAgentChat={isAgentChat}
            mimeType={citation.getMimetype()}
            citationType={{
              link: citationLink,
              pageNumbers: citation.getPageNumber()
                ? [
                    {
                      pageNumber: citation.getPageNumber(),
                      link: citationLink,
                    },
                  ]
                : undefined,
            }}
          />
        )}
      </FlexRow>
    </SourceContainer>
  )
}

// TODO: This will need to be extended to handle different types of sources
export const AnswerSource = ({ source }: { source: Source }): JSX.Element => {
  const { openModal } = useModalState()
  const { setCurrentDocumentSource } = useDocumentSourceContext()
  const summary = source.getSummary()
  const caption = source.getName()
  const sourceType = source.getType()
  const docSourceInfo = source.getDocumentSourceInfo()
  const relevantDate = docSourceInfo?.getRelevantDate()
  const isDeleted = docSourceInfo?.getIsDeleted()
  const isPolicySource = sourceType === SOURCE_TYPE.POLICY_DOC_SOURCE
  const isDocumentSource = getIsDocumentSource(sourceType)
  const onClick = () => {
    if (!isDocumentSource) {
      return
    }
    setCurrentDocumentSource(source)
    openModal(ModalFormId.VIEW_DOCUMENT_SOURCE)
  }

  return (
    <SourceContainer key={source.getId()}>
      <FlexRow gap={8} justify={FlexAlign.FLEX_START}>
        {isPolicySource && <DocumentSourcePolicyIcon />}
        <span>{caption}</span>
        <DocumentSourceRelevantDate>
          {relevantDate && formatISODateTime(relevantDate)}
        </DocumentSourceRelevantDate>
        {isDeleted ? (
          <SourceDeleted>(Deleted)</SourceDeleted>
        ) : (
          <>
            {isDocumentSource && (
              <DocumentSourceLinkIcon
                onClick={(e) => {
                  e.stopPropagation()
                  onClick()
                }}
              />
            )}
          </>
        )}
      </FlexRow>
      {summary && (
        <SourceSummary>
          <Markup markdown={summary} />
        </SourceSummary>
      )}
    </SourceContainer>
  )
}

export const AnswerSources = ({
  questionId,
  questionnaireType,
}: {
  questionId: string
  questionnaireType: QUESTIONNAIRE_TYPE | undefined
}): JSX.Element | null => {
  const { activeEventKey } = useContext(AccordionContext)
  const { data, isLoading, error } = useAnswerSources(
    questionId,
    questionnaireType,
    isAccordionItemSelected(activeEventKey, questionId),
  )

  const sources = useMemo(() => data?.getSourcesList() || [], [data])
  const citations = useMemo(() => data?.getCitationsList() || [], [data])

  if (error) {
    log.error(
      `Error fetching answer sources for question id ${questionId}`,
      error,
    )
    showInfoToast(
      'Sorry, there was an issue fetching the sources for this answer',
    )
    return null
  }

  return (
    <>
      {isLoading ? (
        <TableListLoader />
      ) : citations.length > 0 ? (
        <AnswerCitations citations={citations} mt={8} />
      ) : (
        <>
          {sources.length > 0 && (
            <QASourcesContainer>
              <div>Evidence and Sources</div>
              {sources.map((source) => (
                <AnswerSource key={source.getId()} source={source} />
              ))}
            </QASourcesContainer>
          )}
        </>
      )}
    </>
  )
}

export type AnswerCitationsProps = {
  citations?: ChatCitation[]
  mt?: number
  isAgentChat?: boolean
}

export const AnswerCitations = ({
  citations: allCitations,
  mt = 0,
  isAgentChat = false,
}: AnswerCitationsProps): JSX.Element | null => {
  const citations = useMemo(
    () => allCitations?.filter((citation) => citation.getCaption()),
    [allCitations],
  )

  if (!citations || citations.length === 0) {
    return null
  }

  return (
    <>
      {citations.length > 0 && (
        <QASourcesContainer $mt={mt} $isAgentChat={isAgentChat}>
          {!isAgentChat && <div>Evidence and Sources</div>}
          {citations.map((citation) => (
            <AnswerCitation
              key={`${citation.getType()}-${citation.getReferenceId()}`}
              citation={citation}
              isAgentChat={isAgentChat}
            />
          ))}
        </QASourcesContainer>
      )}
    </>
  )
}

export const AnswerAgainButton = ({
  onClickFunc,
}: {
  onClickFunc: () => void
}): JSX.Element => (
  <StyledScanAgainContainer>
    <TextButton
      onClick={onClickFunc}
      color={palette.purple[400]}
      requiredPermissions={[PERMISSIONS.ASK_QUESTIONS]}
    >
      <FlexRow gap={3}>
        <FontAwesomeIcon icon={faRefresh} />
        <StyledScanAgainP>Answer Again</StyledScanAgainP>
      </FlexRow>
    </TextButton>
  </StyledScanAgainContainer>
)

const AnswerRationale = ({ rationale }: { rationale: string }): JSX.Element => {
  const [isOpen, setIsOpen] = useState<boolean>(false)

  return (
    <FlexColumn align={FlexAlign.FLEX_START} $mt={10}>
      <TextButton
        onClick={() => setIsOpen(!isOpen)}
        color={palette.purple[400]}
      >
        <FlexRow gap={3}>
          <FontAwesomeIcon icon={isOpen ? faChevronUp : faChevronDown} />
          Why this answer
        </FlexRow>
      </TextButton>
      {isOpen && (
        <P $isDim $mt={5}>
          <Markup markdown={rationale} />
        </P>
      )}
    </FlexColumn>
  )
}

export const QuestionnaireAccordionAnswer = ({
  question,
  hasKbSources,
}: {
  question: Question
  hasKbSources: boolean
}): JSX.Element => {
  const { data: questionnaire } = useGetQuestionnaire({
    id: question.getQuestionnaireId(),
  })
  const isAnswering = useRef<boolean>(false)
  const [isNewAnswer, setIsNewAnswer] = useState<boolean>(false)
  const isBuyerRole = useIsBuyer()
  // we want to italicize the text if it is an AI response
  const isGenerated = question.getStatus() === ANSWER_STATUS.GENERATED
  // we want to show ero if the scan is in progress or if it is an AI response
  const canAnswer = question.getCanAnswer()
  const inProgress = question.getInProgress()
  const answer = question.getAnswer()
  const generatedAnswer = useTypewriter({
    text: answer,
    shouldType: isNewAnswer,
  })
  const status = question.getStatus()
  const isManualEdit = status === ANSWER_STATUS.USER_EDITED
  const questionnaireType = questionnaire?.getQuestionnaireType()
  const hasCiteSources = !!questionnaire?.getChatContext()

  useEffect(() => {
    if (inProgress) {
      isAnswering.current = true
    }
    if (!inProgress && isAnswering.current) {
      setIsNewAnswer(true)
      isAnswering.current = false
    }
  }, [inProgress])

  const showAnswer = isGenerated && isNewAnswer ? generatedAnswer : answer
  // Ensure loading state is also working when lambda in progress
  let msg: string | JSX.Element = answer.length ? (
    <Markup markdown={showAnswer} />
  ) : inProgress ? (
    <AICopilotLoading />
  ) : (
    'None'
  )
  // Show default message if the Lambda fails or produces no results and the user has not manually overridden the answer
  if (!answer.length && !canAnswer && isGenerated) {
    const hasFailed = question.getType() === ANSWER_TYPE.UNSUCCESSFUL
    msg = (
      <EroDefaultAnswer
        hasFailed={hasFailed}
        isBuyerRole={isBuyerRole}
        isNewAnswer={isNewAnswer}
      />
    )
  }
  const isAccepted = question.getAcceptance() === ANSWER_ACCEPTANCE.ACCEPTED
  const readableDuration = `Answered in ${getHumanReadableWall(
    Number(question.getWallTime().toFixed(2)),
  )}`

  const showMetaData = !!(isGenerated && answer.length)
  // Knowledge base sources render through the same generic citation path as
  // every other source type. Older answers that predate citations still have
  // KB sources, and GetAnswerSources serves those too (from either
  // AnswerSources or the older QuestionAnswerReferences).
  const showNewSources =
    !isManualEdit && !!answer.length && (hasCiteSources || hasKbSources)
  const rationale = question.getAnswerRationale()
  const showRationale = isGenerated && !inProgress && !!rationale.length

  return (
    <FlexColumn justify={FlexAlign.SPACE_BETWEEN} align={FlexAlign.FLEX_START}>
      <FlexColumn align={FlexAlign.FLEX_START} justify={FlexAlign.FLEX_START}>
        <QuestionnaireAnswerTitle>Answer</QuestionnaireAnswerTitle>
        <FlexRow gap={10} $mt={5}>
          <QuestionnaireP
            $fontSize={16}
            $isItalicized={inProgress || (isGenerated && !isAccepted)}
          >
            {msg}
          </QuestionnaireP>
        </FlexRow>
        {showRationale && <AnswerRationale rationale={rationale} />}
        {showNewSources && (
          <AnswerSources
            questionId={question.getId()}
            questionnaireType={questionnaireType}
          />
        )}
        {/* Only show if! AI Answer and answer has value */}
        {showMetaData && (
          <P $isDim $isItalicized $mt={10}>
            {readableDuration}
            {getReadableAnswerType(question.getType(), questionnaireType)}
          </P>
        )}
      </FlexColumn>
    </FlexColumn>
  )
}

export type InlinePageNumberType = {
  pageNumber: number
  onClick: () => void
}

export type InlineCitationType = {
  onClick: () => void
  pageNumbers?: InlinePageNumberType[]
}

export type LinkPageNumberType = {
  pageNumber: number
  link: string
}

export type LinkCitationType = {
  link: string
  pageNumbers?: LinkPageNumberType[]
}

export type CitationType = InlineCitationType | LinkCitationType

const isInlineCitationType = (
  citationType: CitationType,
): citationType is InlineCitationType => {
  return 'onClick' in citationType
}

const isLinkCitationType = (
  citationType: CitationType,
): citationType is LinkCitationType => {
  return 'link' in citationType
}

const isLinkPageNumberType = (
  pageNumber: LinkPageNumberType | InlinePageNumberType,
): pageNumber is LinkPageNumberType => {
  return 'link' in pageNumber
}

export type CitationLinkProps = {
  citationType: CitationType
  mimeType?: string
  isAgentChat?: boolean
}

export const CitationLink = ({
  citationType,
  mimeType,
  isAgentChat = false,
}: CitationLinkProps): JSX.Element | null => {
  if (isInlineCitationType(citationType)) {
    return (
      <>
        <CitationPageNumber
          pageNumbers={citationType.pageNumbers}
          mimeType={mimeType}
          isAgentChat={isAgentChat}
        />
        <DocumentSourceLinkIcon
          onClick={(e) => {
            e.stopPropagation()
            citationType.onClick()
          }}
        />
      </>
    )
  }

  if (isLinkCitationType(citationType)) {
    return (
      <>
        <CitationPageNumber
          pageNumbers={citationType.pageNumbers}
          mimeType={mimeType}
          isAgentChat={isAgentChat}
        />
        <Link to={citationType.link} target="_blank" rel="noreferrer">
          <DocumentSourceLinkIcon />
        </Link>
      </>
    )
  }

  return null
}

export type CitationPageNumberProps = {
  pageNumbers?: LinkPageNumberType[] | InlinePageNumberType[]
  mimeType?: string
  isAgentChat?: boolean
}

export const CitationPageNumber = ({
  pageNumbers,
  mimeType,
  isAgentChat = false,
}: CitationPageNumberProps): JSX.Element | null => {
  const isPdf = mimeType === 'application/pdf'

  const pageNumberItems = useMemo(() => {
    if (!pageNumbers) {
      return []
    }
    return pageNumbers
      .sort((a, b) => a.pageNumber - b.pageNumber)
      .map((pageNumberItem, index) => (
        <div
          key={pageNumberItem.pageNumber}
          style={{ display: 'inline-block' }}
        >
          {isLinkPageNumberType(pageNumberItem) ? (
            <PageNumberItemLabelLink
              to={pageNumberItem.link}
              target="_blank"
              rel="noreferrer"
              $isAgentChat={isAgentChat}
            >
              {pageNumberItem.pageNumber}
            </PageNumberItemLabelLink>
          ) : (
            <PageNumberItemLabel
              $isAgentChat={isAgentChat}
              onClick={pageNumberItem.onClick}
            >
              {pageNumberItem.pageNumber}
            </PageNumberItemLabel>
          )}
          {index < pageNumbers.length - 1 && <span> | </span>}
        </div>
      ))
  }, [pageNumbers, isAgentChat])

  if (!pageNumbers?.length || !isPdf) {
    return null
  }

  return (
    <PageNumberLabel $isAgentChat={isAgentChat}>
      (PDF Page {pageNumberItems})
    </PageNumberLabel>
  )
}
